import * as THREE from "three";
import { isTouchDevice } from "./deviceUtils.js";

// A "screen" is any object implementing this loose interface:
//   async enter(manager, options)   -> set up DOM/3D for this screen
//   exit()                          -> tear down DOM listeners (keep assets)
//   dispose()                       -> optional, free GPU resources for good
//   update(deltaTime)               -> optional, per-frame logic
//   onResize(sizes)                 -> optional, camera aspect updates
//   scene / camera                  -> getters, or null if nothing to render
//
// Only one screen is ever active. The manager owns the single
// WebGLRenderer/canvas/clock so screens never fight over them.
//
// Unchanged from the original - included here only so the outputs folder
// is a complete, drop-in replacement for the src/ directory.
export class SceneManager {
  constructor(canvas) {
    this.canvas = canvas;
    // Soft shadows + a high device pixel ratio are the two biggest GPU
    // costs in this scene (toon shading itself is cheap) - both scale
    // directly with fragment count, which is exactly what's punishing on
    // phone GPUs, so touch devices get a cheaper tier of each.
    this.isMobile = isTouchDevice();
    this.maxPixelRatio = this.isMobile ? 1.5 : 2;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: !this.isMobile });
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = this.isMobile ? THREE.PCFShadowMap : THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.sizes = { width: window.innerWidth, height: window.innerHeight };
    this.renderer.setSize(this.sizes.width, this.sizes.height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.maxPixelRatio));
    this.clock = new THREE.Clock();
    this.previousTime = 0;
    this.currentScreen = null;
    window.addEventListener("resize", () => this._onResize());
    this._raf = this._raf.bind(this);
    requestAnimationFrame(this._raf);
  }
  _onResize() {
    this.sizes.width = window.innerWidth;
    this.sizes.height = window.innerHeight;
    this.renderer.setSize(this.sizes.width, this.sizes.height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.maxPixelRatio));
    this.currentScreen?.onResize?.(this.sizes);
  }
  async switchTo(screen, options = {}) {
    if (this.currentScreen === screen) return;
    if (this.currentScreen) {
      this.currentScreen.exit?.();
    }
    this.currentScreen = screen;
    await screen.enter?.(this, options);
    screen.onResize?.(this.sizes);
  }
  _raf() {
    const elapsed = this.clock.getElapsedTime();
    const deltaTime = Math.min(elapsed - this.previousTime, 0.1);
    this.previousTime = elapsed;
    this.currentScreen?.update?.(deltaTime);
    const scene = this.currentScreen?.scene;
    const camera = this.currentScreen?.camera;
    if (scene && camera) {
      this.renderer.render(scene, camera);
    }
    requestAnimationFrame(this._raf);
  }
}
